import { FC, Ref, useEffect } from "react";
import { Control, FieldValues } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetCountryListForDropdowns } from "../../../../api/services/client/clientApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiSelectWithTextInput,
  SearchSaveFiltersSubHeader,
} from "../../../../components";
import { MAX_LENGTH_50 } from "../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { toggleToast } from "../../../../redux/slices/appState/appStateSlice";
import {
  OptionListType,
  ProgramTemplateSearchFormKey,
  ProgramTemplateSearchFormProps,
} from "../../modelsAndSchemas/Models";
import { programTemplateSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ProgramTemplateSearchFormWrapper } from "./ProgramTemplateSearchForm.style";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";

interface ProgramTemplateSearchFormSearchProps extends FieldValues {
  control: Control<ProgramTemplateSearchFormProps>;
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
  handleSaveFilter: () => void;
  disableActionBtn: boolean;
  pageId: string;
  programFrameList: OptionListType;
  lineOfBusinessList: OptionListType;
  disabledSearch: boolean;
  countryRef: Ref<{ resetValues: () => void }>;
  showAsModal?: boolean;
  anchorRef?: React.RefObject<HTMLButtonElement>;
}

export const ProgramTemplateSearchForm: FC<
  ProgramTemplateSearchFormSearchProps
> = (props) => {
  const {
    control,
    handleClearAllSearch,
    handleSearchClick,
    handleSaveFilter,
    handleSavedFilter,
    disableActionBtn,
    pageId,
    programFrameList,
    lineOfBusinessList,
    disabledSearch,
    countryRef,
    anchorRef,
    showAsModal,
    isValidForm,
  } = props;

  const { isMobile } = useMediaQueries();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { fetchLabel, commonLabels, fetchComponentTitle, fetchDropdownValues, commonValidationMessages } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();
  const { data: countryListData, isError } = useGetCountryListForDropdowns();
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isError]);

  const renderTemplateProgramHeader = () => {
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={handleSavedFilter}
        onResetAllClick={handleClearAllSearch}
        isSavedFilterDisabled={disableActionBtn}
        anchorRef={anchorRef}
        resetButtonTestId={`${pageId}-clearAll`}
        savedFilterTestId={`${pageId}-savedFilter`}
      />
    );
  };

  return (
    <ProgramTemplateSearchFormWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(
              programTemplateSiteCoreKeys.searchFormKey,
            )}
          />
        }
        isExpanded={!isMobile}
      >
        {renderTemplateProgramHeader()}
        <form data-testid="search-form">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProgramTemplateSearchFormKey.programTemplateName}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(
                    programTemplateSiteCoreKeys.searchFormKey,
                    programTemplateSiteCoreKeys.filterByList,
                  )}
                  label={fetchLabel(
                    programTemplateSiteCoreKeys.searchFormKey,
                    programTemplateSiteCoreKeys.programTemplateNameLabel,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={
                    ProgramTemplateSearchFormKey.programTemplateNameFilter
                  }
                  id={`${pageId}-${ProgramTemplateSearchFormKey.programTemplateName}`}
                  selectBoxId={`${pageId}-${ProgramTemplateSearchFormKey.programTemplateNameFilter}`}
                  maxLength={MAX_LENGTH_50}
                  data-testid="program-name"
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProgramTemplateSearchFormKey.lineOfBusiness}
              >
                <MuiSelect
                  label={fetchLabel(
                    programTemplateSiteCoreKeys.searchFormKey,
                    programTemplateSiteCoreKeys.productLineLabel,
                  )}
                  options={lineOfBusinessList}
                  visibleDefaultSelectOption={true}
                  value=""
                  id={`${pageId}-${ProgramTemplateSearchFormKey.lineOfBusiness}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProgramTemplateSearchFormKey.programFrameWork}
              >
                <MuiSelect
                  label={fetchLabel(
                    programTemplateSiteCoreKeys.searchFormKey,
                    programTemplateSiteCoreKeys.programFrameworkLabel,
                  )}
                  options={programFrameList}
                  value=""
                  id={`${pageId}-${ProgramTemplateSearchFormKey.programFrameWork}`}
                  visibleDefaultSelectOption={true}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProgramTemplateSearchFormKey.status}
              >
                <MuiSelect
                  label={fetchLabel(
                    programTemplateSiteCoreKeys.searchFormKey,
                    programTemplateSiteCoreKeys.statusLabel,
                  )}
                  options={fetchDropdownValues(
                    programTemplateSiteCoreKeys.searchFormKey,
                    programTemplateSiteCoreKeys.statusList,
                  )}
                  value=""
                  id={`${pageId}-${ProgramTemplateSearchFormKey.status}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ProgramTemplateSearchFormKey.country}
                ref={countryRef}
              >
                <MuiMultiSelect
                  label={fetchLabel(
                    programTemplateSiteCoreKeys.searchFormKey,
                    programTemplateSiteCoreKeys.countryLabel,
                  )}
                  options={setApiDataToDropDownOption(countryListData, true)}
                  showSearch
                  showChip
                  id={`${pageId}-${ProgramTemplateSearchFormKey.country}`}
                  chipId={`${pageId}-${ProgramTemplateSearchFormKey.country}-chip`}
                  dialogTitle={commonLabels.selectedCountries}
                  allARIAProps={multiSelectARIALabels}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid
            container
            spacing={isMobile ? 2 : 0}
            className="button-container"
          >
            <MuiButtonSecondary
              onClick={handleSaveFilter}
              id={`${pageId}-saveFilter`}
              disabled={!isValidForm}
            >
              {commonLabels.saveFilter}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              onClick={handleSearchClick}
              disabled={disabledSearch}
              id={`${pageId}-search`}
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </ProgramTemplateSearchFormWrapper>
  );
};
